import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TimeOffModal from './TimeOffModal';
import MyScheduleModal, { WhoWithMeModal, UpcomingTimeOffModal } from './MyScheduleModal';
import { jobName } from '../utils/ui';
import FourQuestions, { EMPTY_ANSWERS, composeUpdate, hasAnswer } from '../components/FourQuestions';
import { MyHours } from '../components/MyInfo';
import QbConfirmReminder from '../components/QbConfirmReminder';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; }
}
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers },
  }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));
}

// "Open in Fieldwire" for the job picked (or clocked into) — field supervisors (Ryan, Oct 11 2026).
// Opens the job's Fieldwire plans in the Fieldwire app / a new tab; says so when the job has none.
function FieldwireButton({ projectId }) {
  const [link, setLink] = useState(null);
  useEffect(() => {
    setLink(null);
    if (!projectId) return;
    let live = true;
    apiFetch(`/time/fieldwire-link?project_id=${encodeURIComponent(projectId)}`).then(d => { if (live) setLink(d); }).catch(() => { if (live) setLink({ openUrl: null }); });
    return () => { live = false; };
  }, [projectId]);
  if (!projectId || !link) return null;
  if (!link.openUrl) return <div style={{ textAlign: 'center', marginTop: 10, fontSize: 13, color: '#9ca3af' }}>This job has no Fieldwire project.</div>;
  return (
    <a href={link.openUrl} target="_blank" rel="noreferrer"
      style={{ display: 'block', boxSizing: 'border-box', width: '100%', textAlign: 'center', marginTop: 10, padding: '14px', borderRadius: 12, border: '2px solid #0f766e', background: '#f0fdfa', color: '#0f766e', fontSize: 15, fontWeight: 800, textDecoration: 'none' }}>
      📐 Open in Fieldwire ↗
    </a>
  );
}

function fmtDuration(seconds) {
  if (!seconds && seconds !== 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}
function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
// Great-circle distance in miles.
function haversineMiles(a, b) {
  const R = 3958.8;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
function fmtDist(mi) {
  if (mi == null) return '';
  if (mi < 0.08) return 'here';
  if (mi < 10) return `${mi.toFixed(1)} mi`;
  return `${Math.round(mi)} mi`;
}
// Clean job number: legacy projects store an 8-char hex id; the real number is
// the start of the name ("2311 - NDOT…").
function jobNum(j) {
  const pn = (j.project_number || '').trim();
  if (pn && !/^[0-9a-f]{8}$/i.test(pn)) return pn;
  const m = (j.name || '').match(/^\s*(WO\s?\d{3,6}|\d{3,6})/i);
  return m ? m[1].replace(/\s+/g, '') : '';
}

// Heat (≥90°F) / cold (<30°F) advisory shown to field crews at clock-in.
function WeatherAdvisoryModal({ advisory, onClose }) {
  const heat = advisory.kind === 'heat';
  const theme = heat
    ? { bg: '#7c2d12', accent: '#f97316', soft: '#fff7ed', icon: '🥵', title: 'Heat Advisory' }
    : { bg: '#1e3a8a', accent: '#2563eb', soft: '#eff6ff', icon: '🥶', title: 'Cold Advisory' };
  const tips = heat ? [
    'Drink water often — about 1 cup every 15–20 minutes; don’t wait until you’re thirsty.',
    'Take rest breaks in shade or A/C; more breaks as it gets hotter.',
    'Watch each other for dizziness, nausea, headache, or confusion.',
    'Heat stroke is an emergency — confusion, hot/dry or clammy skin, fainting: cool the worker and CALL 911.',
  ] : [
    'Dress in warm layers; keep a dry set of clothes and gloves.',
    'Take warm-up breaks out of the wind; keep moving to stay warm.',
    'Watch for shivering, numbness, slurred speech, or clumsiness.',
    'Suspected hypothermia/frostbite is an emergency — get the worker warm and CALL 911.',
  ];
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, width: 400, maxWidth: '100%', overflow: 'hidden', boxShadow: '0 16px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ background: theme.bg, color: '#fff', padding: '22px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 46, lineHeight: 1 }}>{theme.icon}</div>
          <div style={{ fontSize: 20, fontWeight: 900, marginTop: 8, letterSpacing: 0.3 }}>{theme.title}</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4, opacity: 0.95 }}>
            {advisory.feelsF != null
              ? `Feels like ${advisory.feelsF}°F at your job site${advisory.feelsF !== advisory.tempF ? ` (air ${advisory.tempF}°F)` : ''}`
              : `${advisory.tempF}°F at your job site`}
          </div>
        </div>
        <div style={{ padding: '18px 20px' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: theme.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Stay safe today</div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tips.map((t, i) => (
              <li key={i} style={{ display: 'flex', gap: 10, fontSize: 14, color: '#1f2937', lineHeight: 1.4 }}>
                <span style={{ color: theme.accent, fontWeight: 900, flexShrink: 0 }}>•</span><span>{t}</span>
              </li>
            ))}
          </ul>
          <button onClick={onClose} style={{ width: '100%', marginTop: 18, padding: 15, borderRadius: 12, border: 'none', background: theme.accent, color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TimeTracking() {
  const navigate = useNavigate();
  const [status, setStatus]       = useState(null);
  const [clockable, setClockable] = useState([]);   // my active jobs {id,name,project_number,lat,lng}
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [working, setWorking]     = useState(false);
  const [qbRemind, setQbRemind]   = useState(null);   // 'in' | 'switch' — tap Confirm in QuickBooks Time
  const [error, setError]         = useState('');
  const [elapsed, setElapsed]     = useState(0);
  const [showTimeOff, setShowTimeOff] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showHours, setShowHours] = useState(false);   // My Hours by day, right here on the Time screen
  const [showWithMe, setShowWithMe] = useState(false);
  const [showUpcomingOff, setShowUpcomingOff] = useState(false);
  // End-of-day job update, asked of field supervisors and shop crew when they
  // clock out of a job. { project, text } while open, null when closed.
  const [updatePrompt, setUpdatePrompt] = useState(null);
  const [postingUpdate, setPostingUpdate] = useState(false);
  const [updatePosted, setUpdatePosted] = useState(false);
  const asksForUpdate = (() => {
    try {
      const w = JSON.parse(localStorage.getItem('te_worker') || 'null');
      // Field supervisors only, per Ryan. Shop crew clock into the Shop rather than a
      // job, so asking them "how did it go on that job" was the wrong question.
      return w?.role === 'FieldSupervisor';
    } catch { return false; }
  })();

  const [mode, setMode]           = useState('mine'); // 'mine' | 'number' | 'near'
  const [sel, setSel]             = useState(null);   // { key:'project:ID'|'wo:ID', label, coords }
  const [numQuery, setNumQuery]   = useState('');
  const [numResults, setNumResults] = useState([]);
  const [gps, setGps]             = useState(null);   // { withLoc:[], without:[] }
  const [gpsCoords, setGpsCoords] = useState(null);
  const [gpsBusy, setGpsBusy]     = useState(false);
  const [gpsErr, setGpsErr]       = useState('');
  const [showWO, setShowWO]       = useState(false);
  const [switching, setSwitching] = useState(false);  // "Switch Job" tapped — finder is open over a live shift
  const [mineFilter, setMineFilter] = useState('');
  const [advisory, setAdvisory]   = useState(null); // { kind:'heat'|'cold', tempF, feelsF }
  const timerRef = useRef(null);

  useEffect(() => {
    loadStatus();
    apiFetch('/time/clockable-jobs').then(d => setClockable(Array.isArray(d) ? d : [])).catch(() => {});
    apiFetch('/time/work-orders').then(d => setWorkOrders(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (status?.clockedIn && status.shift?.start) {
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - new Date(status.shift.start).getTime()) / 1000));
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [status]);

  async function loadStatus(silent = false) {
    if (!silent) setLoading(true);
    try {
      const s = await apiFetch('/time/status');
      setStatus(s);
      if (s.clockedIn && s.shift?.start) setElapsed(Math.floor((Date.now() - new Date(s.shift.start).getTime()) / 1000));
    } catch (e) {
      setError('Could not load time status.');
    } finally {
      if (!silent) setLoading(false);
    }
  }

  function pick(key, label, coords) { setSel({ key, label, coords: coords || null }); setError(''); }

  // "Open in Fieldwire" under Clock In / Clock Out — field supervisors only (Ryan, Oct 11 2026).
  const isFieldSupervisor = asksForUpdate;

  // Heat/cold advisory is for field roles only — not office/admin.
  const OFFICE_ROLES = ['admin', 'Admin', 'ProjectManager', 'OfficeStaff', 'Accounting'];
  function isFieldRole() {
    try {
      const s = JSON.parse(localStorage.getItem('titan_session') || 'null');
      const role = s?.role || s?.user?.role;
      return !OFFICE_ROLES.includes(role);
    } catch { return true; }
  }
  // Best coordinates for the job just clocked into: the selected job's own
  // location, else the matching active job's, else the phone's GPS fix.
  function advisoryCoords() {
    if (sel?.coords?.lat != null) return sel.coords;
    if (sel?.key?.startsWith('project:')) {
      const j = clockable.find(x => x.id === sel.key.slice(8));
      if (j && j.lat != null && j.lng != null) return { lat: j.lat, lng: j.lng };
    }
    if (gpsCoords?.lat != null) return gpsCoords;
    return null;
  }
  // Best-effort: stamp the phone's location so an assigned truck can show near
  // this person on the equipment map. Silent if location is unavailable/denied.
  function captureMyLocation() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => { apiFetch('/me/location', { method: 'POST', body: JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude }) }).catch(() => {}); },
      () => {}, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  }

  async function checkWeatherAdvisory() {
    if (!isFieldRole()) return;
    const c = advisoryCoords();
    if (!c) return; // no location to check — skip silently
    try {
      const w = await apiFetch(`/weather?lat=${c.lat}&lng=${c.lng}`);
      if (w && w.advisory) setAdvisory({ kind: w.advisory, tempF: w.tempF, feelsF: w.feelsF });
    } catch { /* best-effort */ }
  }

  async function searchNumber(q) {
    setNumQuery(q);
    if (!q.trim()) { setNumResults([]); return; }
    try { setNumResults(await apiFetch(`/time/find-job?q=${encodeURIComponent(q.trim())}`) || []); }
    catch { setNumResults([]); }
  }

  function useMyLocation() {
    if (!navigator.geolocation) { setGpsErr('This device can’t share location.'); return; }
    setGpsErr(''); setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        const me = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setGpsCoords(me);
        const withLoc = clockable.filter(j => j.lat != null && j.lng != null)
          .map(j => ({ ...j, dist: haversineMiles(me, { lat: j.lat, lng: j.lng }) }))
          .sort((a, b) => a.dist - b.dist);
        const without = clockable.filter(j => j.lat == null || j.lng == null);
        setGps({ withLoc, without });
        setGpsBusy(false);
      },
      () => { setGpsErr('Couldn’t get your location. Allow location access and try again.'); setGpsBusy(false); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }

  async function handleClockIn() {
    if (!sel) { setError('Pick a job first.'); return; }
    setError(''); setWorking(true);
    try {
      const [type, id] = sel.key.split(':');
      const body = type === 'wo'
        ? { jobcodeId: parseInt(id) }
        : { projectId: id, ...(sel.coords ? { lat: sel.coords.lat, lng: sel.coords.lng } : {}) };
      if (inNote.trim()) body.notes = inNote.trim();
      const result = await apiFetch('/time/clock-in', { method: 'POST', body: JSON.stringify(body) });
      setInNote(''); setLiveSaved(''); setLiveOpen(false); setLiveNote('');
      if (result?.shift) {
        setStatus({ clockedIn: true, shift: result.shift });
        setElapsed(Math.floor((Date.now() - new Date(result.shift.start).getTime()) / 1000));
      } else {
        await loadStatus(true);
      }
      setQbRemind('in');      // QuickBooks Time holds the location back until they tap Confirm there
      checkWeatherAdvisory(); // heat/cold advisory pop-up (field roles)
      captureMyLocation();    // rough clock-in GPS → places an assigned truck on the map
    } catch (e) {
      setError(e?.error || e?.message || 'Clock-in failed. Please try again.');
    } finally {
      setWorking(false);
    }
  }

  // Move to another job without clocking out and back in. The shift stays
  // continuous — the old entry closes and the new one opens on the same timestamp,
  // so there's no gap in the day and payroll sees one shift across two jobs.
  async function handleSwitchJob() {
    if (!sel) { setError('Pick the job you’re moving to.'); return; }
    setError(''); setWorking(true);
    try {
      const [type, id] = sel.key.split(':');
      const body = type === 'wo' ? { jobcodeId: parseInt(id) } : { projectId: id };
      const r = await apiFetch('/time/switch-job', { method: 'POST', body: JSON.stringify(body) });
      // Nothing moved — say why instead of quietly staying put (Matthew Thiessen, Oct 19 2026:
      // "it won't let me change the job … it just keeps the previous one").
      if (r?.unchanged) { setError(r.message || 'You’re already on that job.'); if (r.same_job) { setSwitching(false); setSel(null); } return; }
      if (r?.shift) {
        setStatus({ clockedIn: true, shift: r.shift });
        setElapsed(Math.floor((Date.now() - new Date(r.shift.start).getTime()) / 1000));
      } else {
        await loadStatus(true);
      }
      setSwitching(false); setSel(null); setQbRemind('switch');
      setLiveSaved(''); setLiveOpen(false); setLiveNote('');
      // The shift just closed, so a payroll note can go on it.
      setJustClosed(r?.fromId || null); setNoLunch(false); setShiftNote(''); setNoteSaved('');
      // Offer a note on the job they just left, same as clocking out of it.
      if (r?.previous?.id && asksForUpdate) setUpdatePrompt({ project: r.previous, text: '', answers: EMPTY_ANSWERS, kind: 'switch' });
      else if (r?.fromId) setNoteSheet('switch');
      captureMyLocation();
    } catch (e) {
      setError(e?.error || e?.message || 'Could not switch jobs. Try again.');
    } finally {
      setWorking(false);
    }
  }

  // The exact words payroll already looks for — 317 timesheets say this and nothing
  // else, so the button says it too rather than adding a ninth spelling.
  const NO_LUNCH = 'No lunch';

  // The note payroll reads, written onto the QuickBooks timesheet itself. "No lunch"
  // is a button rather than something to type, because it is the one that changes pay
  // and the one people forget — and it is asked at the moment it is still true.
  const [justClosed, setJustClosed] = useState(null);
  const [noLunch, setNoLunch] = useState(false);
  const [shiftNote, setShiftNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteSaved, setNoteSaved] = useState('');
  // Pat, Sep 24 2026: everyone gets a place for a payroll note, not only the field
  // supervisors who get the end-of-day prompt — at clock-in, while on the clock, and
  // on the way out (or on the shift just left when switching jobs).
  const [inNote, setInNote] = useState('');           // typed before Clock In, goes on the sheet as it opens
  const [noteSheet, setNoteSheet] = useState(null);   // 'out' | 'switch' — the standalone payroll sheet
  const [liveOpen, setLiveOpen] = useState(false);    // "Add a note" on the running shift
  const [liveNote, setLiveNote] = useState('');
  const [liveSaving, setLiveSaving] = useState(false);
  const [liveSaved, setLiveSaved] = useState('');

  async function saveLiveNote() {
    const text = liveNote.trim();
    if (!text || !shift?.id) return;
    setLiveSaving(true);
    try {
      const r = await apiFetch('/time/note', { method: 'POST', body: JSON.stringify({ timesheetId: shift.id, notes: text }) });
      setLiveSaved(r?.notes || text); setLiveNote(''); setLiveOpen(false);
      setStatus(s => (s?.shift ? { ...s, shift: { ...s.shift, notes: r?.notes || text } } : s));
    } catch (e) { setError(e?.error || 'Could not save that note.'); }
    setLiveSaving(false);
  }

  // The "For payroll" box: a No lunch button and a line of text, written onto the
  // QuickBooks timesheet itself. Shown inside the supervisors' end-of-day prompt and,
  // for everyone else, on its own sheet.
  function renderPayrollBox(onLeftShift) {
    return (
      <div style={{ border: '1.5px solid #fde68a', background: '#fffbeb', borderRadius: 12, padding: 12, marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          For payroll{onLeftShift ? ' — on the shift you just left' : ''}
        </div>
        {noteSaved ? (
          <div style={{ fontSize: 14, color: '#15803d', fontWeight: 700, marginTop: 8 }}>✓ Saved to your timesheet: “{noteSaved}”</div>
        ) : (<>
          <button onClick={() => setNoLunch(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', marginTop: 8, padding: '12px 14px',
              borderRadius: 10, border: `2px solid ${noLunch ? '#b45309' : '#e5e7eb'}`,
              background: noLunch ? '#fef3c7' : '#fff', cursor: 'pointer', textAlign: 'left' }}>
            <span style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, border: `2px solid ${noLunch ? '#b45309' : '#d1d5db'}`,
              background: noLunch ? '#b45309' : '#fff', color: '#fff', fontSize: 15, fontWeight: 800, lineHeight: '18px', textAlign: 'center' }}>{noLunch ? '✓' : ''}</span>
            <span style={{ fontSize: 15.5, fontWeight: 800, color: '#111827' }}>I did not take a lunch</span>
          </button>
          <input value={shiftNote} onChange={e => setShiftNote(e.target.value)}
            placeholder="Anything else payroll should know…"
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 8, padding: '11px 12px', borderRadius: 10,
              border: '1.5px solid #e5e7eb', fontSize: 16, fontFamily: 'inherit' }} />
          <button onClick={saveShiftNote} disabled={savingNote || (!noLunch && !shiftNote.trim())}
            style={{ width: '100%', marginTop: 8, padding: 12, borderRadius: 10, border: 'none',
              background: (noLunch || shiftNote.trim()) ? '#b45309' : '#e5e7eb',
              color: (noLunch || shiftNote.trim()) ? '#fff' : '#9ca3af', fontSize: 14.5, fontWeight: 800,
              cursor: (noLunch || shiftNote.trim()) ? 'pointer' : 'default' }}>
            {savingNote ? 'Saving…' : 'Save to my timesheet'}
          </button>
        </>)}
      </div>
    );
  }

  async function saveShiftNote() {
    if (!justClosed) return;
    const text = [noLunch ? NO_LUNCH : '', shiftNote.trim()].filter(Boolean).join(' — ');
    if (!text) return;
    setSavingNote(true);
    try {
      await apiFetch('/time/note', { method: 'POST', body: JSON.stringify({ timesheetId: justClosed, notes: text }) });
      setNoteSaved(text);
    } catch (e) { setError(e?.error || 'Could not save that note.'); }
    setSavingNote(false);
  }

  async function handleClockOut() {
    setError(''); setWorking(true);
    try {
      const r = await apiFetch('/time/clock-out', { method: 'POST', body: '{}' });
      setStatus({ clockedIn: false, shift: null }); setElapsed(0); setSel(null);
      // The shift that just closed, so a note about it can be written straight onto it.
      setJustClosed(r?.shift?.id || null);
      setNoLunch(false); setShiftNote(''); setNoteSaved('');
      setLiveSaved(''); setLiveOpen(false); setLiveNote('');
      // Ask the field/shop crew how the day went on the job they just left. The
      // clock-out has already gone through — this never blocks it, and it's
      // skippable. Only asked when we could work out which job they were on.
      if (r?.project?.id && asksForUpdate) setUpdatePrompt({ project: r.project, text: '', answers: EMPTY_ANSWERS });
      else if (r?.shift?.id) setNoteSheet('out');
    } catch (e) {
      setError(e?.error || e?.message || 'Clock-out failed. Please try again.');
    } finally {
      setWorking(false);
    }
  }

  // Post the end-of-day note to the job's Daily Updates (same place the Daily
  // Report goes), then close the prompt. A failure is shown but never retried
  // automatically — they're already clocked out.
  async function postClockOutUpdate() {
    // Four questions → one update with a label per line (Ryan, Oct 14 2026 — Field Supervisor Scores).
    const note = composeUpdate(updatePrompt?.answers).trim();
    if (!note || postingUpdate) return;
    setPostingUpdate(true);
    try {
      await apiFetch(`/projects/${updatePrompt.project.id}/daily-updates`, { method: 'POST', body: JSON.stringify({ notes: note }) });
      setUpdatePrompt(null); setUpdatePosted(true);
      setTimeout(() => setUpdatePosted(false), 4000);
    } catch (e) {
      setError(e?.error || e?.message || 'Could not post that update.');
      setUpdatePrompt(null);
    } finally { setPostingUpdate(false); }
  }

  const clockedIn = status?.clockedIn;
  const shift = status?.shift;

  const TopBar = () => (
    <div className="top-bar">
      <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#111' }}>
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M14 4l-7 7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      <div className="top-bar-wordmark" style={{ flex: 1, textAlign: 'center' }}>TITAN <span>ELECTRIC</span></div>
      <div style={{ width: 22 }} />
    </div>
  );

  if (loading) {
    return (
      <div className="page" style={{ background: '#fff' }}>
        <TopBar />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: '#999', fontSize: 15 }}>Loading…</div>
      </div>
    );
  }

  const tab = (key, label) => (
    <button onClick={() => setMode(key)} style={{
      flex: 1, padding: '10px 6px', borderRadius: 10, border: 'none', cursor: 'pointer',
      background: mode === key ? '#111' : '#f3f4f6', color: mode === key ? '#fff' : '#374151',
      fontSize: 13, fontWeight: 700,
    }}>{label}</button>
  );

  const jobCard = (j, extra, coords) => (
    <button key={j.id} onClick={() => pick(`project:${j.id}`, j.name, coords)}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '13px 14px', marginBottom: 8,
        borderRadius: 12, cursor: 'pointer', textAlign: 'left',
        border: `2px solid ${sel?.key === `project:${j.id}` ? '#16a34a' : '#e5e7eb'}`,
        background: sel?.key === `project:${j.id}` ? '#f0fdf4' : '#fff',
      }}>
      {jobNum(j) && <span style={{ fontSize: 12, fontWeight: 800, color: '#e87722', fontFamily: 'monospace', flexShrink: 0 }}>{jobNum(j)}</span>}
      <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis' }}>{jobName(j)}</span>
      {extra != null && <span style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', flexShrink: 0 }}>{extra}</span>}
      {sel?.key === `project:${j.id}` && <span style={{ color: '#16a34a', fontSize: 18, flexShrink: 0 }}>✓</span>}
    </button>
  );

  return (
    <div className="page" style={{ background: '#fff' }}>
      {qbRemind && <QbConfirmReminder kind={qbRemind} onClose={() => setQbRemind(null)} />}
      <TopBar />
      <div style={{ padding: '20px 20px 110px', maxWidth: 480, margin: '0 auto' }}>

        {/* Status card */}
        <div style={{ borderRadius: 16, background: clockedIn ? '#f0fdf4' : '#f9fafb', border: `2px solid ${clockedIn ? '#16a34a' : '#e5e7eb'}`, padding: '22px 20px', marginBottom: 20, textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: clockedIn ? 14 : 4 }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: clockedIn ? '#16a34a' : '#9ca3af', boxShadow: clockedIn ? '0 0 0 4px rgba(22,163,74,0.2)' : 'none' }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: clockedIn ? '#15803d' : '#6b7280' }}>{clockedIn ? 'Clocked In' : 'Not Clocked In'}</span>
          </div>
          {clockedIn && (<>
            <div style={{ fontSize: 42, fontWeight: 900, color: '#111', fontVariantNumeric: 'tabular-nums', letterSpacing: -1, marginBottom: 4 }}>{fmtDuration(elapsed)}</div>
            <div style={{ fontSize: 14, color: '#666' }}>Started at {fmtTime(shift?.start)}</div>
            {shift?.jobcodeName && <div style={{ display: 'inline-block', marginTop: 10, background: '#e87722', color: '#fff', borderRadius: 20, padding: '3px 14px', fontSize: 13, fontWeight: 700 }}>{shift.jobcodeName}</div>}
            {/* A note on the running shift — written now, while it is still true. */}
            {(liveSaved || shift?.notes) && !liveOpen && (
              <div style={{ marginTop: 10, fontSize: 13, color: '#92400e', fontWeight: 600 }}>📝 {liveSaved || shift.notes}</div>
            )}
            {shift?.id && !liveOpen && (
              <button onClick={() => { setLiveOpen(true); setError(''); }}
                style={{ marginTop: 8, background: 'none', border: 'none', color: '#1d4ed8', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', padding: 4 }}>
                ✎ {(liveSaved || shift?.notes) ? 'Add to the note' : 'Add a note for payroll'}
              </button>
            )}
            {liveOpen && (
              <div style={{ marginTop: 10, textAlign: 'left' }}>
                <input value={liveNote} onChange={e => setLiveNote(e.target.value)} autoFocus
                  placeholder="No lunch, started at the yard, left early…"
                  onKeyDown={e => { if (e.key === 'Enter') saveLiveNote(); }}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '11px 12px', borderRadius: 10, border: '1.5px solid #fde68a', background: '#fffbeb', fontSize: 16, fontFamily: 'inherit' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={saveLiveNote} disabled={liveSaving || !liveNote.trim()}
                    style={{ flex: 1, padding: 11, borderRadius: 10, border: 'none', background: liveNote.trim() ? '#b45309' : '#e5e7eb', color: liveNote.trim() ? '#fff' : '#9ca3af', fontSize: 14, fontWeight: 800, cursor: liveNote.trim() ? 'pointer' : 'default' }}>
                    {liveSaving ? 'Saving…' : 'Save to my timesheet'}
                  </button>
                  <button onClick={() => { setLiveOpen(false); setLiveNote(''); }}
                    style={{ padding: '11px 14px', borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
          </>)}
        </div>

        {/* Job finder — when clocked out, or when switching to a different job */}
        {(!clockedIn || switching) && (<>
          {/* Make it unmistakable that the clock keeps running — this looks exactly
              like the clock-in screen otherwise, and picking a job here does
              something quite different. */}
          {switching && (
            <div style={{ background: '#eff6ff', border: '2px solid #93c5fd', borderRadius: 12, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#1d4ed8', marginBottom: 3 }}>Moving to another job</div>
              <div style={{ fontSize: 13, color: '#1e3a5f', lineHeight: 1.45 }}>
                You stay clocked in — your time keeps running. Pick where you're heading and your hours move over from{shift?.jobcodeName ? ' ' : ' the current job'}<strong>{shift?.jobcodeName || ''}</strong>.
              </div>
            </div>
          )}
          {/* Selected banner */}
          {sel && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#ecfdf5', border: '2px solid #6ee7b7', borderRadius: 12, padding: '11px 14px', marginBottom: 14 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: '#059669', textTransform: 'uppercase', letterSpacing: 0.5 }}>Selected</span>
              <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: '#065f46', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sel.label}</span>
              <button onClick={() => setSel(null)} style={{ background: 'none', border: 'none', color: '#059669', fontSize: 18, cursor: 'pointer', padding: 0 }}>×</button>
            </div>
          )}

          {/* Finder tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {tab('mine', 'My Jobs')}
            {tab('number', 'Job #')}
            {tab('near', '📍 Near Me')}
          </div>

          {/* My Jobs */}
          {mode === 'mine' && (
            <div style={{ marginBottom: 8 }}>
              {clockable.length > 6 && (
                <input value={mineFilter} onChange={e => setMineFilter(e.target.value)} placeholder="Filter my jobs…"
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, boxSizing: 'border-box', marginBottom: 10 }} />
              )}
              {(() => {
                if (clockable.length === 0) return <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 14, padding: '20px 0' }}>You’re not on any active jobs. Use <strong>Job #</strong> to clock into one.</div>;
                const f = mineFilter.trim().toLowerCase();
                const list = f ? clockable.filter(j => (j.name || '').toLowerCase().includes(f) || jobNum(j).toLowerCase().includes(f)) : clockable;
                if (list.length === 0) return <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 14, padding: '14px 0' }}>No job matches “{mineFilter}”.</div>;
                return list.map(j => jobCard(j, null, null));
              })()}
            </div>
          )}

          {/* Job # entry */}
          {mode === 'number' && (
            <div style={{ marginBottom: 8 }}>
              <input value={numQuery} onChange={e => searchNumber(e.target.value)} autoFocus
                inputMode="text" placeholder="Type a job number, e.g. 2629 or WO26901"
                style={{ width: '100%', padding: '13px 14px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 16, boxSizing: 'border-box', marginBottom: 10 }} />
              {numQuery.trim() && numResults.length === 0 && <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 14, padding: '10px 0' }}>No active job matches “{numQuery}”.</div>}
              {numResults.map(j => jobCard(j, null, null))}
            </div>
          )}

          {/* Near Me */}
          {mode === 'near' && (
            <div style={{ marginBottom: 8 }}>
              {!gps && (
                <button onClick={useMyLocation} disabled={gpsBusy}
                  style={{ width: '100%', padding: '14px', borderRadius: 12, border: '2px solid #2563eb', background: '#eff6ff', color: '#1d4ed8', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
                  {gpsBusy ? 'Locating…' : '📍 Use my location'}
                </button>
              )}
              {gpsErr && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14, marginTop: 10 }}>{gpsErr}</div>}
              {gps && (<>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '4px 2px 10px' }}>
                  <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Nearest jobs first</span>
                  <button onClick={useMyLocation} style={{ background: 'none', border: 'none', color: '#2563eb', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>↻ Refresh</button>
                </div>
                {gps.withLoc.length === 0 && <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: '6px 0 14px' }}>None of your jobs have a map location set yet. The office can set one on the job’s “Pick on Map”.</div>}
                {gps.withLoc.map(j => jobCard(j, fmtDist(j.dist), null))}
                {gps.without.length > 0 && (<>
                  <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, margin: '10px 2px 6px' }}>No map location</div>
                  {gps.without.map(j => jobCard(j, null, null))}
                </>)}
              </>)}
            </div>
          )}

          {/* Other work orders (T-Sheets) */}
          {workOrders.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <button onClick={() => setShowWO(v => !v)} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '4px 0' }}>
                {showWO ? '▾' : '▸'} Other work orders ({workOrders.length})
              </button>
              {showWO && (
                <select value={sel?.key?.startsWith('wo:') ? sel.key.slice(3) : ''} onChange={e => { const wo = workOrders.find(w => String(w.id) === e.target.value); if (wo) pick(`wo:${wo.id}`, wo.name, null); }}
                  style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #ddd', fontSize: 15, marginTop: 6 }}>
                  <option value="">Select a work order…</option>
                  {workOrders.map(wo => <option key={wo.id} value={wo.id}>{wo.name}</option>)}
                </select>
              )}
            </div>
          )}
        </>)}

        {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 14, margin: '16px 0' }}>{error}</div>}

        {/* Action button */}
        {!clockedIn ? (<>
          {/* Optional. Goes onto the timesheet as it opens, where payroll reads it. */}
          <input value={inNote} onChange={e => setInNote(e.target.value)}
            placeholder="Note for payroll (optional) — late start, started at the yard…"
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 16, padding: '11px 12px', borderRadius: 10, border: '1.5px solid #e5e7eb', fontSize: 15, fontFamily: 'inherit' }} />
          <button onClick={handleClockIn} disabled={working || !sel}
            style={{ width: '100%', padding: '16px', borderRadius: 12, border: 'none', marginTop: 10, background: (!working && sel) ? '#16a34a' : '#d1d5db', color: '#fff', fontSize: 17, fontWeight: 800, cursor: (!working && sel) ? 'pointer' : 'not-allowed' }}>
            {working ? 'Clocking In…' : 'Clock In'}
          </button>
          {isFieldSupervisor && <FieldwireButton projectId={sel ? sel.key.split(':')[1] : status?.scheduledProjectId} />}
        </>) : switching ? (
          <>
            <button onClick={handleSwitchJob} disabled={working || !sel}
              style={{ width: '100%', padding: '16px', borderRadius: 12, border: 'none', marginTop: 18, background: (!working && sel) ? '#2563eb' : '#d1d5db', color: '#fff', fontSize: 17, fontWeight: 800, cursor: (!working && sel) ? 'pointer' : 'not-allowed' }}>
              {working ? 'Switching…' : sel ? 'Switch to this job' : 'Pick a job above'}
            </button>
            <button onClick={() => { setSwitching(false); setSel(null); setError(''); }} disabled={working}
              style={{ width: '100%', padding: '13px', borderRadius: 12, border: '1.5px solid #d1d5db', marginTop: 10, background: '#fff', color: '#6b7280', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              Cancel — stay on this job
            </button>
          </>
        ) : (
          <>
            <button onClick={() => { setSwitching(true); setSel(null); setError(''); setMode('mine'); }} disabled={working}
              style={{ width: '100%', padding: '15px', borderRadius: 12, border: '2px solid #2563eb', background: '#eff6ff', color: '#1d4ed8', fontSize: 16, fontWeight: 800, cursor: 'pointer', marginBottom: 10 }}>
              ⇄ Switch Job
            </button>
            <button onClick={handleClockOut} disabled={working}
              style={{ width: '100%', padding: '16px', borderRadius: 12, border: 'none', background: working ? '#d1d5db' : '#dc2626', color: '#fff', fontSize: 17, fontWeight: 800, cursor: working ? 'not-allowed' : 'pointer' }}>
              {working ? 'Clocking Out…' : 'Clock Out'}
            </button>
            {isFieldSupervisor && <FieldwireButton projectId={status?.projectId} />}
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 12, fontSize: 13, color: '#9ca3af' }}>Time is recorded in T-Sheets automatically.</div>

        {/* Two by two (Ryan, Oct 11 2026): Schedule · Who's with me / Request Time Off · Upcoming vacations. */}
        {/* Room at the foot so the floating bell never sits on the bottom-right button. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 22, marginBottom: 72 }}>
          {/* My hours, day by day — this week and last, straight from QuickBooks Time (Oct 21 2026). */}
          <button onClick={() => setShowHours(true)}
            style={{ gridColumn: '1 / -1', padding: '14px 8px', borderRadius: 12, border: '2px solid #1e3a5f', background: '#f8fafc', color: '#1e3a5f', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            ⏱️ My Hours — by day
          </button>
          <button onClick={() => setShowSchedule(true)}
            style={{ padding: '14px 8px', borderRadius: 12, border: '2px solid #2563eb', background: '#eff6ff', color: '#1d4ed8', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            📅 Schedule
          </button>
          <button onClick={() => setShowWithMe(true)}
            style={{ padding: '14px 8px', borderRadius: 12, border: '2px solid #2563eb', background: '#eff6ff', color: '#1d4ed8', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            👷 Who's with me
          </button>
          <button onClick={() => setShowTimeOff(true)}
            style={{ padding: '14px 8px', borderRadius: 12, border: '2px solid #111', background: '#fff', color: '#111', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            Request Time Off
          </button>
          <button onClick={() => setShowUpcomingOff(true)}
            style={{ padding: '14px 8px', borderRadius: 12, border: '2px solid #15803d', background: '#f0fdf4', color: '#15803d', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            🌴 Upcoming vacations
          </button>
        </div>
      </div>

      {advisory && <WeatherAdvisoryModal advisory={advisory} onClose={() => setAdvisory(null)} />}

      {showSchedule && <MyScheduleModal onClose={() => setShowSchedule(false)} />}
      {showHours && (
        <div onClick={() => setShowHours(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 5000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#f6f5f2', width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', borderRadius: '18px 18px 0 0', padding: '16px 16px 28px', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ flex: 1, fontSize: 18, fontWeight: 800 }}>⏱️ My Hours</div>
              <button onClick={() => setShowHours(false)} aria-label="Close" style={{ width: 36, height: 36, borderRadius: 999, border: 'none', background: '#e5e7eb', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <MyHours />
          </div>
        </div>
      )}
      {showWithMe && <WhoWithMeModal onClose={() => setShowWithMe(false)} />}
      {showUpcomingOff && <UpcomingTimeOffModal onClose={() => setShowUpcomingOff(false)} />}

      {showTimeOff && <TimeOffModal onClose={() => setShowTimeOff(false)} />}

      {updatePosted && (
        <div style={{ position: 'fixed', left: 16, right: 16, bottom: 90, zIndex: 6000, background: '#15803d', color: '#fff', borderRadius: 10, padding: '12px 16px', fontSize: 14, fontWeight: 700, textAlign: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.25)' }}>
          ✓ Update posted — thanks
        </div>
      )}

      {/* The payroll note on its own, for everyone who does not get the end-of-day
          prompt. They are already clocked out (or already on the new job) — this is
          optional and skippable. */}
      {noteSheet && !updatePrompt && justClosed && (
        <div onClick={() => setNoteSheet(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 6000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', width: '100%', maxWidth: 480, borderRadius: '18px 18px 0 0', padding: '18px 18px 26px', paddingBottom: 'calc(26px + env(safe-area-inset-bottom))', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: noteSheet === 'switch' ? '#1d4ed8' : '#15803d', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {noteSheet === 'switch' ? '⇄ Switched jobs' : '✓ Clocked out'}
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#111827', marginTop: 6, marginBottom: 12 }}>
              Anything payroll should know?
            </div>
            {renderPayrollBox(noteSheet === 'switch')}
            <button onClick={() => setNoteSheet(null)}
              style={{ width: '100%', padding: 14, borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              {noteSaved ? 'Done' : 'Nothing to add'}
            </button>
          </div>
        </div>
      )}

      {/* End-of-day update. They're already clocked out; this is optional and
          skippable, and posts to the job's Daily Updates. */}
      {updatePrompt && (
        <div onClick={() => setUpdatePrompt(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 6000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', width: '100%', maxWidth: 480, borderRadius: '18px 18px 0 0', padding: '18px 18px 26px', paddingBottom: 'calc(26px + env(safe-area-inset-bottom))', maxHeight: '90vh', overflowY: 'auto' }}>
            {/* Same prompt serves clocking out and switching jobs — say which, because
                "Clocked out" on a switch is simply wrong and they're still on the clock. */}
            <div style={{ fontSize: 12, fontWeight: 800, color: updatePrompt.kind === 'switch' ? '#1d4ed8' : '#15803d', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {updatePrompt.kind === 'switch' ? '⇄ Switched jobs' : '✓ Clocked out'}
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#111827', marginTop: 6 }}>
              {updatePrompt.kind === 'switch' ? 'Before you move on…' : "How'd it go today?"}
            </div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3, marginBottom: 12 }}>
              A quick update for <b style={{ color: '#111827' }}>{jobName(updatePrompt.project) || updatePrompt.project.name}</b> — what got done, what's left, anything the office should know.
            </div>

            {/* Goes on the timesheet in QuickBooks, where payroll reads it — not into
                the job update, which nobody runs payroll from. */}
            {justClosed && renderPayrollBox(updatePrompt.kind === 'switch')}
            <FourQuestions value={updatePrompt.answers} autoFocus onChange={a => setUpdatePrompt(p => ({ ...p, answers: a }))} />
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={postClockOutUpdate} disabled={postingUpdate || !hasAnswer(updatePrompt.answers)}
                style={{ flex: 1, padding: 14, borderRadius: 10, border: 'none', background: hasAnswer(updatePrompt.answers) ? '#15803d' : '#cbd5e1', color: '#fff', fontSize: 15, fontWeight: 800, cursor: hasAnswer(updatePrompt.answers) ? 'pointer' : 'default' }}>
                {postingUpdate ? 'Posting…' : 'Post Update'}
              </button>
              <button onClick={() => setUpdatePrompt(null)}
                style={{ padding: '14px 18px', borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Skip</button>
            </div>
          </div>
        </div>
      )}

      <div className="bottom-nav">
        <button className="nav-item" onClick={() => navigate('/dashboard')}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="2" y="2" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" /><rect x="12" y="2" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" /><rect x="2" y="12" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" /><rect x="12" y="12" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" /></svg>
          <span>Jobs</span>
        </button>
        <button className="nav-item active">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="8.5" stroke="currentColor" strokeWidth="1.5" fill="none" /><path d="M11 6.5V11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          <span>Time</span>
        </button>
        <button className="nav-item" onClick={() => navigate('/ask')}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 2C6.03 2 2 5.8 2 10.5c0 2.3.97 4.38 2.55 5.9L3.5 20l4.13-1.5C8.9 18.83 9.93 19 11 19c4.97 0 9-3.8 9-8.5S15.97 2 11 2Z" stroke="currentColor" strokeWidth="1.5" fill="none" /><path d="M8 10.5h6M8 7.5h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          <span>Ask AI</span>
        </button>
      </div>
    </div>
  );
}
